import {
  DebitCardFundingLimitOptionsResult,
  DebitCardFundingLimitOptionsResultTypenames,
} from '@varo/graphql-schema-types';
import { useMemo } from 'react';
import TabaPayDebitCardFundingLimitOptions from 'services/graphql/queries/MoveMoney/TabaPayDebitCardFundingLimitOptions';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

const useTabaPayDebitCardFundingLimitOptions = () => {
  const [{ data, fetching, error }] = useQuery<{
    debitCardFundingLimitOptions: DebitCardFundingLimitOptionsResult;
  }>({
    query: TabaPayDebitCardFundingLimitOptions,
    requestPolicy: 'network-only',
  });

  const responseError = useFormattedCombinedError(error);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const debitCardFundingLimitOptionsData = data?.debitCardFundingLimitOptions;

  const successData =
    debitCardFundingLimitOptionsData?.__typename ===
    DebitCardFundingLimitOptionsResultTypenames.DebitCardFundingLimitOptionsSuccess
      ? debitCardFundingLimitOptionsData
      : undefined;

  const notEligibleData =
    debitCardFundingLimitOptionsData?.__typename ===
    DebitCardFundingLimitOptionsResultTypenames.DebitCardFundingLimitOptionsNotEligible
      ? debitCardFundingLimitOptionsData
      : undefined;

  // Fail closed: the user is only eligible when the query returns a Success
  // result that actually carries the enforced min/max limits. A missing
  // typename, GraphQL/network error, NotEligible response, or even a Success
  // payload without amounts all leave isEligible false, so the transfer form
  // never renders with undefined min/max amounts and bypasses risk-limit
  // enforcement. (Boolean(successData) alone is not enough — a well-typed but
  // partial Success object would be truthy.)
  const hasEnforceableLimits = Boolean(
    successData?.minAmount && successData?.maxAmount,
  );
  const isEligible = hasEnforceableLimits;

  const minCardDebitFundingAmount = successData?.minAmount;
  const maxCardDebitFundingAmount = successData?.maxAmount;
  const limitErrorMessage = successData?.limitErrorMessage ?? '';

  const errorScreen = useMemo(() => {
    if (responseError) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      };
    }
    if (notEligibleData) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: notEligibleData.title,
        messageBody: notEligibleData.message,
        primaryButton: notEligibleData.button,
      };
    }
    // Fail closed: once the query has resolved without an enforceable Success
    // result (unknown typename, or a Success missing min/max limits) and
    // without a recognized error/NotEligible response, surface a generic error
    // screen so the form never renders without enforced limits.
    if (!fetching && !hasEnforceableLimits) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: defaultMessage,
      };
    }
    return null;
  }, [
    responseError,
    notEligibleData,
    fetching,
    hasEnforceableLimits,
    defaultTitle,
    defaultMessage,
  ]);

  return {
    errorScreen,
    isEligible,
    data,
    fetching,
    minCardDebitFundingAmount,
    maxCardDebitFundingAmount,
    limitErrorMessage,
  };
};

export default useTabaPayDebitCardFundingLimitOptions;
